// apps/web/src/components/GemBalance/GemBalance.tsx
// ─────────────────────────────────────────────────────────────────────────
// Gem chip + lightweight Rewards popover (NOT a full rewards page).
// Popover shows: current streak + status message, Streak Freezes owned, a
// CALM contextual quick-add when the streak is at risk (seatbelt, never a
// siren), next reward hint, and a single CTA. The old "gems today / cap"
// meter is gone — organic earning is uncapped.
//
// The chip is now a real toggle. Clicking opens/closes the popover; click
// outside or Escape closes it. This removes the "looks clickable, does
// nothing" trust gap.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
	selectGemBalance,
	purchaseStreakFreeze,
} from '../../store/economySlice';
import { MAX_STREAK_FREEZES } from '@nisk/core/srs';
import { STREAK_FREEZE_COST } from '@nisk/core/economy';
import { useStreak } from '../../shared/useStreak';
import { useContinueLearning } from '../../shared/useContinueLearning';
import type { AppDispatch } from '../../store';
import styles from './GemBalance.module.scss';
import { useEscapeKey, useOutsideClick } from '../../hooks';

interface GemBalanceProps {
	variant?: 'pill' | 'compact';
}

const GemIcon: React.FC = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'>
		<path
			d='M12 2L4 9l8 13 8-13z'
			fill='currentColor'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinejoin='round'
		/>
		<path
			d='M4 9h16M12 2v20M8.5 9L12 22M15.5 9L12 22'
			stroke='#0d0d0d'
			strokeWidth='1'
			strokeLinejoin='round'
			opacity='0.55'
		/>
	</svg>
);

const GemBalance: React.FC<GemBalanceProps> = ({ variant = 'pill' }) => {
	const dispatch = useDispatch<AppDispatch>();
	const total = useSelector(selectGemBalance);

	const streak = useStreak();
	const next = useContinueLearning();

	const [pulsing, setPulsing] = useState(false);
	const [open, setOpen] = useState(false);
	const prevTotal = useRef(total);
	const wrapperRef = useRef<HTMLDivElement>(null);

	// Pulse on gem increase
	useEffect(() => {
		if (total > prevTotal.current) {
			setPulsing(true);
			const t = setTimeout(() => setPulsing(false), 700);
			prevTotal.current = total;
			return () => clearTimeout(t);
		}
		prevTotal.current = total;
		return undefined;
	}, [total]);

	// Close on outside click / Escape
	useOutsideClick(wrapperRef, () => setOpen(false), open);
	useEscapeKey(() => setOpen(false), open);

	const displayValue = total.toLocaleString();

	// "Next reward" hint — kept generic and honest: nudge toward the
	// resolved next learning action.
	const nextRewardText =
		next.state === 'fallback'
			? 'Complete a lesson to earn gems'
			: `Keep going: ${next.context ?? 'your next lesson'}`;

	// Streak Freeze quick-add — CONTEXTUAL and calm. Shown only when the
	// streak is actually exposed (at-risk / frozen) and there's room to hold
	// another freeze. Protective framing; the Gem Vault is the normal home.
	const canHoldMore = streak.freezesOwned < MAX_STREAK_FREEZES;
	const streakExposed =
		streak.streakStatus === 'at-risk' || streak.streakStatus === 'frozen';
	const showFreezeQuickAdd = canHoldMore && streakExposed;
	const canAffordFreeze = total >= STREAK_FREEZE_COST;

	const handleAddFreeze = () => {
		if (!canAffordFreeze) return;
		void dispatch(purchaseStreakFreeze());
	};

	return (
		<div
			className={styles.wrapper}
			ref={wrapperRef}>
			<button
				type='button'
				className={`${styles.balance} ${styles[`balance_${variant}`]} ${
					pulsing ? styles.balance_pulsing : ''
				} ${open ? styles.balance_open : ''}`}
				onClick={() => setOpen((o) => !o)}
				aria-haspopup='dialog'
				aria-expanded={open}
				aria-label={`Gem balance: ${total}. ${
					streak.currentStreak > 0
						? `${streak.currentStreak} day streak.`
						: ''
				} Open rewards`}>
				<span
					className={styles.icon}
					aria-hidden='true'>
					<GemIcon />
				</span>
				<span className={styles.count}>{displayValue}</span>
				{streak.currentStreak > 0 && (
					<span
						className={styles.streakInline}
						aria-hidden='true'>
						<span className={styles.flame}>🔥</span>
						{streak.currentStreak}
					</span>
				)}
			</button>

			{open && (
				<div
					className={styles.popover}
					role='dialog'
					aria-label='Rewards'>
					{/* Streak */}
					<div className={styles.row}>
						<span className={styles.rowLabel}>Streak</span>
						<span className={styles.rowValue}>
							{streak.currentStreak > 0
								? `🔥 ${streak.currentStreak} ${
										streak.currentStreak === 1
											? 'day'
											: 'days'
									}`
								: '·'}
						</span>
					</div>
					<div className={styles.streakMsg}>{streak.message}</div>
					<div className={styles.rowFooter}>
						New day at 5:00 PM UTC
					</div>

					{/* Streak Freezes */}
					<div className={styles.row}>
						<span className={styles.rowLabel}>Streak Freezes</span>
						<span className={styles.rowValue}>
							🧊 {streak.freezesOwned} / {MAX_STREAK_FREEZES}
						</span>
					</div>
					{showFreezeQuickAdd && (
						<button
							type='button'
							className={styles.freezeBuy}
							onClick={handleAddFreeze}
							disabled={!canAffordFreeze}>
							<span className={styles.freezeBuyTitle}>
								Get a Streak Freeze · {STREAK_FREEZE_COST} gems
							</span>
							<span className={styles.freezeBuyHint}>
								{canAffordFreeze
									? 'Protects your streak when life gets busy.'
									: `Earn ${
											STREAK_FREEZE_COST - total
										} more gems to get one.`}
							</span>
						</button>
					)}

					<div className={styles.divider} />

					{/* Next reward hint */}
					<div className={styles.nextReward}>
						<span className={styles.nextRewardLabel}>
							Next reward
						</span>
						<span className={styles.nextRewardText}>
							{nextRewardText}
						</span>
					</div>

					{/* Vault link */}
					<Link
						to='/rewards'
						className={styles.vaultLink}
						onClick={() => setOpen(false)}>
						Open Gem Vault →
					</Link>

					{/* CTA */}
					<Link
						to={next.href}
						className={styles.cta}
						onClick={() => setOpen(false)}>
						{next.state === 'fallback'
							? 'Start learning'
							: 'Keep learning'}
					</Link>
				</div>
			)}
		</div>
	);
};

export default GemBalance;
